/**
 * Add / edit a pipeline row. On an existing one the footer also offers logging
 * an activity, drafting outreach, and — once it is won — creating the client
 * record.
 *
 * Setting the stage to Won here does what dropping the card in the Won column
 * does: the row converts, and from then on it is also on the Deals page.
 */
import { useState } from 'react';
import { useApp } from '../../store/AppContext.jsx';
import { formatDate, addDays, toInputDate, fromInputDate } from '../../lib/dates.js';
import {
  assignees, clientOfDeal, isConverted, pipelineStages,
  leadDetails, MANUAL_SOURCE
} from '../../domain/crm.js';
import Sheet, { Group, Field, QuickActions, OptionSelect, RecordSelect } from '../Sheet.jsx';
import DealJourney from '../DealJourney.jsx';

export default function DealSheet({ id, onClose, manual = false }) {
  const {
    S, crm, rows, people, saveDeal, saveCompany, saveContact, convertDeal, convertWon, moveDealStage,
    noteActivity, logDealEvent,
    setSheet, openDraft, toast
  } = useApp();

  const existing = id ? crm.deals.find(d => d.id === id) : null;

  /* Editing starts from what the row already says, address and number
     included - they live in the notes, so they are read back out of it
     rather than left blank for somebody to retype. */
  const [form, setForm] = useState(() => (existing
    ? { ...existing, ...(({ email, phone }) => ({ email, phone }))(leadDetails(crm, existing)) }
    : {
    title: '', cid: '', contact: '',
    /* A manual row starts at the bottom of the board; a deal typed in on the
       Deals page is one already won - that page lists nothing else. */
    stage: manual ? (S.stages || ['New'])[0] : 'Won',
    value: '', prob: '',
    close: formatDate(addDays(new Date(), 30), false, S.dateFmt),
    /* Empty, not whoever is signed in. Typing a deal in is not the same as
       taking it on, and a name filled in by the box is a name nobody chose -
       the board then reads as assigned when nothing was assigned. */
    owner: '',
    /* Added to the pipeline by hand: the source says so, and it is the one
       value the sync never writes, so the board can tell these apart. */
    source: manual ? MANUAL_SOURCE : '',
    plan: '', cycle: 'Monthly', next: '', notes: '',
    email: '', phone: '',
    kind: manual ? 'lead' : 'deal'
  }));

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  /*
   * Checked as they type, refused only on the way out.
   *
   * Half an address is not a mistake, it is somebody mid-word, so nothing
   * complains until the field has something in it that cannot become one.
   * Deliberately loose: an address is anything@anything.something and a
   * number is digits with the punctuation people actually use. A stricter
   * rule rejects real addresses and teaches people to fight the form.
   */
  const emailOk = v => !String(v || '').trim() || /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(v).trim());
  const phoneOk = v => !String(v || '').trim() || /^[+]?[\d\s()./-]{6,}$/.test(String(v).trim());
  const emailBad = !emailOk(form.email);
  const phoneBad = !phoneOk(form.phone);

  /*
   * Put the contact into the notes, the way the tracker writes it.
   *
   * The deal row has no column for an address - the tracker keeps it in
   * that first line and every screen reads it back from there with
   * leadDetails. Writing it anywhere else would mean a second place to read
   * it from and a manual row that still showed a dash.
   *
   * The Contact: line is replaced rather than appended, so editing twice
   * does not leave two.
   */
  const withContact = f => {
    const bits = [String(f.email || '').trim(), String(f.phone || '').trim()].filter(Boolean);
    const who = [String(f.title || '').trim(), ...bits].filter(Boolean).join(' · ');
    const lines = String(f.notes || '').split('\n');

    /*
     * A tracker lead keeps its contact inside the first line, after the date
     * of the request, and leadDetails reads the paragraph by position — the
     * date from line one, the campaign and products from line two, the
     * landing page from line three. A Contact: line added above them pushed
     * all three down a line and they were lost on the first save, so on these
     * rows the contact is rewritten in place instead.
     */
    const head = /^(Demo requested \S+\.)/.exec((lines[0] || '').trim());
    if (head) {
      if (bits.length) lines[0] = `${head[1]} Contact: ${who}`;
      return lines.join('\n').trim();
    }

    const rest = lines.filter(l => !/^Contact:/.test(l.trim())).join('\n').trim();
    if (!bits.length) return rest;
    return [`Contact: ${who}`, rest].filter(Boolean).join('\n');
  };

  /* Still on the board: the stage list stops short of Won, and winning is
     the action in the footer - one way to do it, the same on the card, the
     row and here. */
  const onBoard = manual || (!!existing && existing.kind !== 'deal' && existing.origin !== 'xyra');

  const linkedClient = id ? clientOfDeal(rows, existing || id) : null;

  const save = () => {
    if (!String(form.title).trim()) { toast('Name the deal'); return; }
    if (emailBad) { toast('That email address is not right'); return; }
    if (phoneBad) { toast('That phone number is not right'); return; }

    const wasStage = existing?.stage;
    const dealId = saveDeal(id, {
      ...form,
      title: String(form.title).trim(),
      notes: withContact(form)
    });
    onClose();

    /*
     * The board writes a note when a row moves, and this form did not, so
     * a stage changed here left no trace: the journey showed the arrival
     * and then the deal already at Negotiation, with nothing in between.
     * moveDealStage is not the thing to call — saveDeal has already
     * written the stage from the form — so it is just the note.
     */
    if (wasStage && form.stage && form.stage !== wasStage) {
      noteActivity(dealId, `Moved to ${form.stage}`);
      logDealEvent(dealId, form.stage === 'Won' ? 'won' : form.stage === 'Lost' ? 'lost' : 'stage',
        { from: wasStage, to: form.stage, actor: form.owner });
    }

    /* Won is the conversion to a deal, and nothing more; the client is a
       separate decision, taken on the Deals page. */
    if (form.stage === 'Won' && wasStage !== 'Won') convertWon(dealId);
  };

  return (
    <Sheet
      icon="card"
      tone="g-orange"
      title={id ? (onBoard ? 'Edit lead' : 'Edit deal') : manual ? 'Add manual' : 'New lead'}
      onClose={onClose}
      onSave={save}
      saveLabel={manual ? 'Add to pipeline' : onBoard ? 'Save lead' : 'Save deal'}
    >
      <Group>{onBoard ? 'Lead' : 'Deal'}</Group>

      <Field label="Title" wide>
        <input autoFocus value={form.title || ''} onChange={e => set('title', e.target.value)} />
      </Field>

      {/* A name nobody has becomes a record on the way out of the box. */}
      <Field label="Company">
        <RecordSelect
          value={form.cid}
          onChange={v => set('cid', v)}
          records={crm.companies}
          onCreate={name => saveCompany(null, { name, industry: '', size: '', city: '', owner: '', web: '' }, { quiet: true })}
        />
      </Field>

      {/*
        * How to reach them.
        *
        * A row typed in by hand had no address and no number anywhere, while
        * every synced row carries both - so a manual deal arrived on the
        * board as a name nobody could act on. They are written into the
        * notes in the same shape the tracker writes them, which is where
        * every screen already reads them back from.
        */}
      <Field label="Email" hint={emailBad ? <small className="ferr">That is not an email address</small> : null}>
        <input
          type="email"
          value={form.email || ''}
          placeholder="name@company.com"
          className={emailBad ? 'bad' : ''}
          onChange={e => set('email', e.target.value)}
        />
      </Field>

      <Field label="Phone" hint={phoneBad ? <small className="ferr">That is not a phone number</small> : null}>
        <input
          value={form.phone || ''}
          placeholder="+971 50 123 4567"
          className={phoneBad ? 'bad' : ''}
          onChange={e => set('phone', e.target.value)}
        />
      </Field>

      <Field label="Stage">
        <OptionSelect
          value={form.stage}
          onChange={v => set('stage', v)}
          options={onBoard ? pipelineStages(S) : (S.stages || [])}
        />
      </Field>

      <Field label="Owner">
        <OptionSelect value={form.owner} onChange={v => set('owner', v)} options={assignees(people, S)} blank="" />
      </Field>

      {/* Fixed on a manual row: "Manual" is what the source is for, and
          letting it be changed would let the row pass for a tracked one. */}
      <Field label="Source">
        {form.source === MANUAL_SOURCE
          ? <input value={MANUAL_SOURCE} disabled />
          : <OptionSelect value={form.source} onChange={v => set('source', v)} options={S.sources || []} blank="" />}
      </Field>

      <Field label="Plan discussed">
        <OptionSelect value={form.plan} onChange={v => set('plan', v)} options={S.plans || []} blank="" />
      </Field>

      <Field label="Billing cycle">
        <OptionSelect value={form.cycle || 'Monthly'} onChange={v => set('cycle', v)} options={['Monthly', 'Annual']} />
      </Field>

      <Field label="Next step" wide>
        <input value={form.next || ''} onChange={e => set('next', e.target.value)} />
      </Field>

      <Field label="Notes" wide>
        <textarea value={form.notes || ''} onChange={e => set('notes', e.target.value)} />
      </Field>

      {id && form.stage === 'Won' && S.clientsApp ? (
        <Field label="Client account" wide>
          <RecordSelect
            value={form.clientId || ''}
            onChange={v => { saveDeal(id, { clientId: v || null }); onClose(); }}
            records={rows}
            blank="Not linked — pick the account…"
          />
          <div className="muted">
            Accounts are created in your clients app. Link one here so this deal
            stops counting as “won but not yet a client”.
          </div>
        </Field>
      ) : null}

      {/* The trail itself, not a count of it. "3 logged · last touch 2d
          ago" said how much had happened and never what, so anybody who
          wanted to know closed this and opened the view sheet to read the
          same rows from the other side. */}
      {id ? (
        <Field label="Activity" wide>
          <DealJourney
            deal={crm.deals.find(d => d.id === id)}
            limit={6}
            onLog={() => { onClose(); setSheet({ kind: 'activity', dealId: id }); }}
            onEdit={actId => { onClose(); setSheet({ kind: 'activity', id: actId }); }}
          />
          <QuickActions>
            {form.stage === 'Won' && !linkedClient && !S.clientsApp ? (
              <button type="button" onClick={() => { onClose(); convertDeal(id); }}>
                Create the client record
              </button>
            ) : null}
          </QuickActions>
        </Field>
      ) : null}
    </Sheet>
  );
}
